import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PieChart, Info, ShieldCheck, ArrowUpRight, Search, ChevronDown, TrendingUp } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import DataFreshnessStrip from '../components/DataFreshnessStrip';
import { api } from '../api';

function fmtPct(n) { if (n == null) return '—'; return `${Number(n).toFixed(1)}%`; }
function fmtInt(n) { if (n == null) return '—'; return Number(n).toLocaleString(); }

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

function KPI({ label, value, accent }) {
  return (
    <div className="p-4 rounded-xl bg-white border border-[#E5DCC4]">
      <div className={`font-display text-2xl tracking-tight ${accent || 'text-[#1A2622]'}`}>{value}</div>
      <div className="mt-1 text-[10.5px] uppercase tracking-wider text-[#7A857F]">{label}</div>
    </div>
  );
}

function Select({ value, onChange, options, placeholder, testId }) {
  return (
    <div className="relative">
      <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="appearance-none w-full bg-white border border-[#E5DCC4] rounded-lg h-10 px-3 pr-8 text-[12.5px] outline-none cursor-pointer focus:border-[#34538F]">
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={typeof o === 'string' ? o : o.value} value={typeof o === 'string' ? o : o.value}>{typeof o === 'string' ? o : o.label}</option>)}
      </select>
      <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-[#7A857F]" />
    </div>
  );
}

export default function CensusIntelligencePage() {
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [state, setState] = useState('');
  const [minOcc, setMinOcc] = useState('');
  const [minSk, setMinSk] = useState('');
  const [maxMcd, setMaxMcd] = useState('');
  const [minMC, setMinMC] = useState('');
  const [sort, setSort] = useState('opportunity_score');
  const [list, setList] = useState({ items: [], total: 0 });
  const [listLoading, setListLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.censusDashboard()
      .then((d) => { setDash(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setListLoading(true);
      api.censusFacilities({
        q: q || undefined,
        state: state || undefined,
        min_occupancy: minOcc || undefined,
        min_skilled: minSk || undefined,
        max_medicaid: maxMcd || undefined,
        min_managed_care: minMC || undefined,
        sort,
        limit: 30,
      }).then((d) => { setList(d); setListLoading(false); }).catch(() => setListLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, state, minOcc, minSk, maxMcd, minMC, sort]);

  if (loading) {
    return <div className="min-h-screen"><Navbar /><main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-10"><div className="text-sm text-[#7A857F]">Loading Census Intelligence…</div></main></div>;
  }
  if (!dash || dash.ready === false) {
    return (
      <div className="min-h-screen"><Navbar />
        <main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-6 pb-10">
          <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Census Intelligence' }]} />
          <h1 className="font-display text-4xl text-[#1A2622] mt-2">Census & Payer Mix Intelligence</h1>
          <div className="mt-6 rounded-xl bg-white border border-dashed border-[#E5DCC4] p-8 text-center">
            <PieChart size={28} className="mx-auto text-[#A8B0AA]" />
            <p className="mt-3 text-[13px] text-[#7A857F]">{dash?.message || 'Awaiting first sync. Use the Data freshness strip to run.'}</p>
          </div>
        </main>
      </div>
    );
  }

  const n = dash.national || {};

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-6 pb-10">
        <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Census Intelligence' }]} />
        <div className="mt-3"><DataFreshnessStrip /></div>

        <header className="mt-4 mb-6 flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10.5px] font-semibold uppercase tracking-wider bg-[#E0EAF8] text-[#34538F]">
              <PieChart size={11} /> Estimated · public-data only
            </div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-[#1A2622] mt-2">Census & Payer Mix Intelligence</h1>
            <p className="mt-2 text-[13px] text-[#5A6660] max-w-[680px]">
              Estimated short-stay skilled vs long-term-care census, payer mix (Medicare, Medicaid, Managed Care, Private Pay),
              and Census Opportunity Score for every SNF. Public CMS cost-report-derived estimates — never patient-level data.
            </p>
          </div>
        </header>

        {/* National KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="census-kpis">
          <KPI label="Facilities with estimates" value={fmtInt(n.facilities_with_census)} accent="text-[#34538F]" />
          <KPI label="Total certified beds" value={fmtInt(n.total_beds)} />
          <KPI label="Avg occupancy" value={fmtPct(n.avg_occupancy)} accent="text-[#34538F]" />
          <KPI label="Avg short-stay skilled" value={fmtPct(n.avg_pct_skilled)} accent="text-[#34538F]" />
          <KPI label="Avg long-term care" value={fmtPct(n.avg_pct_ltc)} accent="text-[#A06628]" />
        </div>

        {/* National payer split bar */}
        <div className="mt-5 rounded-xl bg-white border border-[#E5DCC4] p-5">
          <h2 className="font-display text-base font-semibold text-[#1A2622]">National payer mix (estimated avg)</h2>
          <div className="mt-3 h-9 rounded-md overflow-hidden bg-[#F0E7D2] flex text-[11.5px] text-white font-semibold">
            <div className="bg-[#34538F] flex items-center justify-center" style={{ width: `${n.avg_pct_medicare || 0}%` }} title={`Medicare ${fmtPct(n.avg_pct_medicare)}`}>{(n.avg_pct_medicare || 0) >= 8 ? `Medicare ${fmtPct(n.avg_pct_medicare)}` : ''}</div>
            <div className="bg-[#5C82C0] flex items-center justify-center" style={{ width: `${n.avg_pct_managed_care || 0}%` }} title={`Managed Care ${fmtPct(n.avg_pct_managed_care)}`}>{(n.avg_pct_managed_care || 0) >= 8 ? `MC ${fmtPct(n.avg_pct_managed_care)}` : ''}</div>
            <div className="bg-[#A06628] flex items-center justify-center" style={{ width: `${n.avg_pct_medicaid || 0}%` }} title={`Medicaid ${fmtPct(n.avg_pct_medicaid)}`}>{(n.avg_pct_medicaid || 0) >= 8 ? `Medicaid ${fmtPct(n.avg_pct_medicaid)}` : ''}</div>
            <div className="bg-[#D58668] flex items-center justify-center" style={{ width: `${n.avg_pct_private_pay || 0}%` }} title={`Private Pay ${fmtPct(n.avg_pct_private_pay)}`}>{(n.avg_pct_private_pay || 0) >= 8 ? `Private ${fmtPct(n.avg_pct_private_pay)}` : ''}</div>
          </div>
          <div className="mt-2 flex flex-wrap gap-4 text-[11px] text-[#5A6660]">
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#34538F] inline-block" /> Medicare</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#5C82C0] inline-block" /> Managed Care (est.)</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#A06628] inline-block" /> Medicaid</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#D58668] inline-block" /> Private Pay (est.)</span>
          </div>
        </div>

        {/* State leaderboard */}
        <section className="mt-5 rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
          <div className="px-5 py-4 border-b border-[#F0E7D2]">
            <h2 className="font-display text-base font-semibold text-[#1A2622]">State leaderboard — avg opportunity score</h2>
            <p className="text-[11.5px] text-[#7A857F]">States ranked by avg facility opportunity score (occupancy + skilled mix + MC growth gap + diversification + alignment).</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="bg-[#F5EFDD] text-[10.5px] uppercase tracking-wider text-[#5A6660]">
                <tr>
                  <th className="text-left px-4 py-2.5">State</th>
                  <th className="text-right px-4 py-2.5">Facilities</th>
                  <th className="text-right px-4 py-2.5">Avg occupancy</th>
                  <th className="text-right px-4 py-2.5">Avg skilled</th>
                  <th className="text-right px-4 py-2.5">Avg Medicaid</th>
                  <th className="text-right px-4 py-2.5">Avg MC (est.)</th>
                  <th className="text-right px-4 py-2.5">Avg score</th>
                </tr>
              </thead>
              <tbody>
                {(dash.state_leaderboard || []).slice(0, 15).map((s) => (
                  <tr key={s.state} className="border-t border-[#F0E7D2]" data-testid={`census-state-${s.state}`}>
                    <td className="px-4 py-2.5 font-semibold text-[#1A2622]">{s.state}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{fmtInt(s.facilities)}</td>
                    <td className="px-4 py-2.5 text-right text-[#34538F] tabular-nums">{fmtPct(s.avg_occupancy)}</td>
                    <td className="px-4 py-2.5 text-right text-[#34538F] tabular-nums">{fmtPct(s.avg_pct_skilled)}</td>
                    <td className="px-4 py-2.5 text-right text-[#A06628] tabular-nums">{fmtPct(s.avg_pct_medicaid)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{fmtPct(s.avg_pct_managed_care)}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-[#34538F]">{s.avg_opportunity_score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Filterable facilities */}
        <section className="mt-5">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <h2 className="font-display text-lg font-semibold text-[#1A2622]">Facilities</h2>
            <span className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">{fmtInt(list.total)} match</span>
            <span className="ml-auto inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-semibold bg-[#E5F0EA] text-[#2D5F4F]">
              <ShieldCheck size={11} /> Public-data estimates · no PHI
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mb-3">
            <div className="relative md:col-span-2">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#7A857F]" />
              <input data-testid="census-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Facility name…" className="w-full bg-white border border-[#E5DCC4] rounded-lg h-10 pl-8 pr-3 text-[12.5px] outline-none focus:border-[#34538F]" />
            </div>
            <Select testId="census-state" value={state} onChange={setState} options={US_STATES} placeholder="State" />
            <Select testId="census-occ" value={minOcc} onChange={setMinOcc} options={[{value:'70',label:'Occ ≥70%'},{value:'80',label:'Occ ≥80%'},{value:'90',label:'Occ ≥90%'}]} placeholder="Min occ" />
            <Select testId="census-skilled" value={minSk} onChange={setMinSk} options={[{value:'15',label:'Skilled ≥15%'},{value:'25',label:'Skilled ≥25%'},{value:'40',label:'Skilled ≥40%'}]} placeholder="Min skilled" />
            <Select testId="census-mcd" value={maxMcd} onChange={setMaxMcd} options={[{value:'60',label:'Medicaid ≤60%'},{value:'45',label:'Medicaid ≤45%'},{value:'30',label:'Medicaid ≤30%'}]} placeholder="Max Medicaid" />
            <Select testId="census-mc" value={minMC} onChange={setMinMC} options={[{value:'5',label:'MC ≥5%'},{value:'10',label:'MC ≥10%'},{value:'20',label:'MC ≥20%'}]} placeholder="Min MC" />
          </div>
          <div className="flex items-center mb-2 text-[11.5px] text-[#7A857F]">
            <span className="ml-auto flex items-center gap-2">
              Sort:
              <select data-testid="census-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="bg-white border border-[#E5DCC4] rounded h-7 px-2 text-[11.5px]">
                <option value="opportunity_score">Opportunity score</option>
                <option value="occupancy">Occupancy</option>
                <option value="skilled">Skilled mix</option>
                <option value="managed_care">Managed Care (est.)</option>
                <option value="medicaid">Medicaid (most)</option>
                <option value="name">Name</option>
              </select>
            </span>
          </div>

          <div className="rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead className="bg-[#F5EFDD] text-[10.5px] uppercase tracking-wider text-[#5A6660]">
                  <tr>
                    <th className="text-left px-4 py-2.5">Facility</th>
                    <th className="text-right px-4 py-2.5">Beds</th>
                    <th className="text-right px-4 py-2.5">Occ.</th>
                    <th className="text-right px-4 py-2.5">Skilled</th>
                    <th className="text-right px-4 py-2.5">LTC</th>
                    <th className="text-right px-4 py-2.5">Medicare</th>
                    <th className="text-right px-4 py-2.5">Medicaid</th>
                    <th className="text-right px-4 py-2.5">MC (est.)</th>
                    <th className="text-right px-4 py-2.5">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {listLoading && <tr><td colSpan={9} className="px-4 py-8 text-center text-[#7A857F]">Loading…</td></tr>}
                  {!listLoading && list.items.length === 0 && (
                    <tr><td colSpan={9} className="px-4 py-8 text-center text-[#7A857F]">No facilities match these filters.</td></tr>
                  )}
                  {!listLoading && list.items.map((f) => (
                    <tr key={f.ccn} className="border-t border-[#F0E7D2] hover:bg-[#FBF6EC]" data-testid={`census-row-${f.ccn}`}>
                      <td className="px-4 py-2.5">
                        <Link to={`/facilities/${f.ccn}`} className="text-[#1A2622] font-medium hover:text-[#34538F]">{f.name}</Link>
                        <div className="text-[10.5px] text-[#7A857F]">{f.city}, {f.state} · CCN {f.ccn}</div>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{fmtInt(f.beds)}</td>
                      <td className="px-4 py-2.5 text-right text-[#34538F] tabular-nums">{fmtPct(f.occupancy_pct)}</td>
                      <td className="px-4 py-2.5 text-right text-[#34538F] tabular-nums font-semibold">{fmtPct(f.pct_skilled_est)}</td>
                      <td className="px-4 py-2.5 text-right text-[#A06628] tabular-nums">{fmtPct(f.pct_ltc_est)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{fmtPct(f.pct_medicare)}</td>
                      <td className="px-4 py-2.5 text-right text-[#A06628] tabular-nums">{fmtPct(f.pct_medicaid)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{fmtPct(f.pct_managed_care_est)}</td>
                      <td className="px-4 py-2.5 text-right font-semibold text-[#34538F]">{f.opportunity_score?.toFixed(0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <div className="mt-5 flex items-start gap-2 text-[11.5px] text-[#A8B0AA] leading-relaxed">
          <Info size={12} className="mt-0.5 shrink-0" />
          <span>
            <strong>Methodology:</strong> {dash.methodology}
            {' '}<strong>Source:</strong> {dash.source}.
          </span>
        </div>
      </main>
      <Footer />
    </div>
  );
}
